/**
 * Two-letter planet codes for chart labels.
 *
 * Study labels are drawn onto the chart itself, where a full label like
 * "Venus Retrograde to Direct" is wide enough to overlap the neighbouring study when several run
 * on one chart. "Ve R2D" carries the same information in a quarter of the width.
 *
 * The codes are the conventional astronomical two-letter forms, which matters for the pairs that
 * collide on their first letter: Mercury/Mars/Moon all start with M, and Saturn/Sun with S. Taking
 * the first two letters of each name would give Me/Ma/Mo and Sa/Su, which happens to be right —
 * but Neptune would become "Ne" and Uranus "Ur" only by luck, and Pluto "Pl" rather than "Pu".
 * The mapping is therefore explicit rather than derived.
 */
export const PLANET_ABBREVIATIONS = {
  Sun: 'Su',
  Moon: 'Mo',
  Mercury: 'Me',
  Venus: 'Ve',
  Mars: 'Ma',
  Jupiter: 'Ju',
  Saturn: 'Sa',
  Uranus: 'Ur',
  Neptune: 'Ne',
  Pluto: 'Pl',
  // Lunar nodes: not planets, but this app treats them as such throughout the ephemeris layer,
  // and a retrograde study can be run on them.
  Rahu: 'Ra',
  Ketu: 'Ke',
};

/**
 * The two-letter code for a planet, or the name unchanged when there is no mapping.
 *
 * Returning the input rather than a placeholder is deliberate: an unmapped body should still be
 * identifiable on the chart. A label reading "?? R2D" would be worse than a long one.
 */
export function abbreviatePlanet(planet) {
  if (planet === null || planet === undefined) return '';
  const raw = String(planet).trim();
  if (!raw) return '';
  const match = Object.keys(PLANET_ABBREVIATIONS).find(p => p.toLowerCase() === raw.toLowerCase());
  return match ? PLANET_ABBREVIATIONS[match] : raw;
}

/** Short code for the two retrograde study directions. */
export const RETRO_DIRECTION_CODES = {
  'retro-direct': 'R2D',   // retrograde turning direct
  'direct-retro': 'D2R',   // direct turning retrograde
};

/** Short codes for the two halves of a Retro-Complement study. */
export const RETRO_COMPLEMENT_CODES = {
  loop: 'RTRO',      // the retrograde loop, from the retrograde station to the direct one
  retrace: 'RTRC',   // the direct advance that regains the degree the loop started from
};

/**
 * The two labels a Retro-Complement study draws, e.g. { loop: "Me RTRO", retrace: "Me RTRC" }.
 *
 * Two labels rather than one, because the study draws two adjacent bands. A single label would
 * name whichever band the reader's eye landed on first, and the whole point of the study is that
 * the two are different periods.
 *
 * @param {string} planet planet name, any casing
 */
export function retroComplementLabels(planet) {
  const abbr = abbreviatePlanet(planet);
  const tag = code => (abbr ? `${abbr} ${code}` : code);
  return { loop: tag(RETRO_COMPLEMENT_CODES.loop), retrace: tag(RETRO_COMPLEMENT_CODES.retrace) };
}

/**
 * Label for a retrograde study, e.g. "Ve R2D".
 *
 * @param {string} planet planet name, any casing
 * @param {boolean} isRetrogradeToDirect true for retrograde→direct, false for direct→retrograde
 */
export function retrogradeStudyLabel(planet, isRetrogradeToDirect) {
  const code = isRetrogradeToDirect ? RETRO_DIRECTION_CODES['retro-direct']
                                    : RETRO_DIRECTION_CODES['direct-retro'];
  const abbr = abbreviatePlanet(planet);
  return abbr ? `${abbr} ${code}` : code;
}
